import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Col, Container, Form, Row } from 'react-bootstrap';
import {
  ActionButtons,
  addStatusToast,
  DataTableItem,
  Loader,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AccountingGroupDetails,
  AccountingsQueryFilters,
  getAllSellAccountingGroupsAsync,
  getAllSellVatsAsync,
  selectSellAccountingGroups,
  selectSellAccountingGroupsLoadingStatus,
  selectSellVats,
  updateSellAccountingGroupAsync,
} from './accountingSlice';
import { LOADING_STATUS } from '../../common/constants';

const SellAccountingGroupDetailsView = () => {
  const backLinkUrl = '/accounting/sellaccountinggroups';
  const params = useParams();
  const dispatch = useAppDispatch();
  const accountingGroups = useAppSelector(selectSellAccountingGroups);
  const accountingGroupsLoadingStatus = useAppSelector(
    selectSellAccountingGroupsLoadingStatus,
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const sellvats = useAppSelector(selectSellVats);

  const [filtering, setFiltering] = useState<AccountingsQueryFilters>({
    pageSize: 200,
    pageNumber: 1,
    culture: 'fi_FI',
  });

  const [selvatsList, setSelvatsList] = useState<DataTableItem[]>([]);
  const [editedAccountingAccountsData, setEditedAccountingAccountsData] =
    useState<AccountingGroupDetails>({
      id: '',
      name: '',
      advancePurchaseAccount: 0,
      discountAccount: 0,
      advanceSalesAccount: 0,
      domesticTravelAgencyCommissionAccount: 0,
      purchaseAccount: 0,
      foreignTravelAgencyCommissionAccount: 0,
      purchaseDeptAccount: 0,
      purchasesWithoutTaxesAccount: 0,
      purchaseVatDeptAccount: 0,
      salesAccount: 0,
      salesClaimAccount: 0,
      salesVatDeptAccount: 0,
      sellerCommissionAccount: 0,
      sellVatId: '',
      purchaseVatId: '',
    });

  const initFetch = useCallback(() => {
    dispatch(getAllSellVatsAsync(filtering));
    dispatch(getAllSellAccountingGroupsAsync(filtering));
  }, [dispatch]);

  useEffect(() => {
    initFetch();
  }, [initFetch]);

  useEffect(() => {
    if (sellvats && sellvats.length > 0) {
      const formattedSelVatsList: DataTableItem[] = [];
      sellvats?.forEach(item => {
        formattedSelVatsList.push({
          ItemId: item.id,
          Items: [item.name],
          Url: `/accounting/sellvats/details/${item.id}`,
        });
      });
      setSelvatsList(formattedSelVatsList);
    }
  }, [sellvats]);

  const sellVatsOptions = [
    { ItemId: '', Items: [''], Url: '' } as DataTableItem,
  ]
    .concat(selvatsList)
    .map(item => (
      <option key={item.ItemId} value={item.ItemId} id={item.ItemId}>
        {item.Items}
      </option>
    ));
  const handleChangeAccountingGroupSellVat = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const sellVatGuid = sellVatsOptions.filter(itemv => value === itemv.key);
    if (sellVatGuid != null) {
      const storededitedGroup: AccountingGroupDetails = {
        ...editedAccountingAccountsData,
        sellVatId: sellVatGuid[0].key.toString(),
      };
      setEditedAccountingAccountsData(storededitedGroup);
    } else {
      const storededitedGroup = {
        ...editedAccountingAccountsData,
        sellVatId: null,
      };
      setEditedAccountingAccountsData(storededitedGroup);
    }
  };

  // Choose group to edit by Id
  useEffect(() => {
    if (params.accountingGroupId && accountingGroups.length > 0) {
      const getAccountingGroupId = accountingGroups.find(
        ({ id }) => id === params.accountingGroupId,
      );
      if (getAccountingGroupId) {
        setEditedAccountingAccountsData(getAccountingGroupId);
      }
    }
  }, [params.accountingGroupId]);

  // Set view loading status
  useEffect(() => {
    if (
      accountingGroupsLoadingStatus !== LOADING_STATUS.LOADING &&
      editedAccountingAccountsData != null &&
      editedAccountingAccountsData.id !== ''
    ) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [accountingGroupsLoadingStatus, editedAccountingAccountsData]);

  useEffect(() => {}, [editedAccountingAccountsData.sellVatId]);
  const handleChangeAccountingAccounts = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    if (editedAccountingAccountsData != null) {
      const storedAccountingAccountsData = {
        ...editedAccountingAccountsData,
        [event.target.name]: value,
      };
      setEditedAccountingAccountsData(storedAccountingAccountsData);
    }
  };

  const saveAccountingAccounts = () => {
    dispatch(updateSellAccountingGroupAsync(editedAccountingAccountsData)).then(
      res3 => {
        dispatch(
          addStatusToast({
            Message: `Asetukset tallennettu onnistuneesti`,
          }),
        );
      },
    );
  };

  return (
    <>
      <Helmet>
        <title>Kirjanpitotilit - Määrityksen tiedot</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Kirjanpito ja Alv-määritykset', 'Kirjanpitotilit']}
          Title={`Kirjanpitotilin tiedot: ${
            editedAccountingAccountsData != null
              ? editedAccountingAccountsData.name
              : ''
          }`}
        />

        <Loader IsLoading={isLoading} />

        {!isLoading && (
          <Container className="container-form mt-48">
            <Form.Group controlId="name" className="mb-40">
              <Form.Label>Nimi</Form.Label>
              <Form.Control
                autoComplete="off"
                value={editedAccountingAccountsData.name}
                onChange={handleChangeAccountingAccounts}
                name="name"
              />
            </Form.Group>

            <Row xs={1} sm={2}>
              <Col>
                {' '}
                <Form.Group controlId="salesAccount" className="mb-40">
                  <Form.Label>Myynnit</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={editedAccountingAccountsData.salesAccount}
                    onChange={handleChangeAccountingAccounts}
                    name="salesAccount"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="advanceSalesAccount" className="mb-40">
                  <Form.Label>Myyntiennakot</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={editedAccountingAccountsData.advanceSalesAccount}
                    onChange={handleChangeAccountingAccounts}
                    name="advanceSalesAccount"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="discountAccount" className="mb-40">
                  <Form.Label>Alennukset</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={editedAccountingAccountsData.discountAccount}
                    onChange={handleChangeAccountingAccounts}
                    name="discountAccount"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="salesVatDeptAccount" className="mb-40">
                  <Form.Label>Myyntien alv-velat</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={editedAccountingAccountsData.salesVatDeptAccount}
                    onChange={handleChangeAccountingAccounts}
                    name="salesVatDeptAccount"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group
                  controlId="domesticTravelAgencyCommissionAccount"
                  className="mb-40"
                >
                  <Form.Label>Jakelukanavaprovisiot 25.5%</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={
                      editedAccountingAccountsData.domesticTravelAgencyCommissionAccount
                    }
                    onChange={handleChangeAccountingAccounts}
                    name="domesticTravelAgencyCommissionAccount"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group
                  controlId="foreignTravelAgencyCommissionAccount"
                  className="mb-40"
                >
                  <Form.Label>Jakelukanavaprovisiot 0 %</Form.Label>
                  <Form.Control
                    autoComplete="off"
                    value={
                      editedAccountingAccountsData.foreignTravelAgencyCommissionAccount
                    }
                    onChange={handleChangeAccountingAccounts}
                    name="foreignTravelAgencyCommissionAccount"
                  />
                </Form.Group>
                <Form.Group controlId="accountinggroup" className="mb-40">
                  <Form.Label>Alv %</Form.Label>
                  {sellVatsOptions.length > 0 && (
                    <Form.Select
                      value={editedAccountingAccountsData.sellVatId}
                      onChange={handleChangeAccountingGroupSellVat}
                      name="sellVatsOptions"
                    >
                      {sellVatsOptions}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <ActionButtons
              CancelButtonText="Peruuta"
              CancelButtonUrl={backLinkUrl}
              HandleSave={saveAccountingAccounts}
            />
          </Container>
        )}
      </Container>
    </>
  );
};

export default SellAccountingGroupDetailsView;
